import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  MapPin,
  LogOut,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Shield,
  CheckCircle2,
  XCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminSecuritySessions() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [revoking, setRevoking] = useState(null);
  const [showRevokeAllDialog, setShowRevokeAllDialog] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);
  const currentSessionId = localStorage.getItem("speakr_session_id");

  useEffect(() => {
    if (user?.id) {
      loadSessions();
    }
  }, [user]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/sessions?user_id=${user.id}`);
      const data = await response.json();
      setSessions(data);
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load active sessions");
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    setRevoking(sessionId);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/sessions/${sessionId}?user_id=${user.id}`, {
        method: "DELETE"
      });
      
      if (!response.ok) throw new Error("Failed to revoke session");
      
      toast.success("Session revoked successfully");
      loadSessions();
    } catch (error) {
      toast.error("Failed to revoke session");
    } finally {
      setRevoking(null);
    }
  };

  const revokeAllSessions = async () => {
    setRevokingAll(true);
    try {
      const response = await fetch(
        `${API_URL}/api/v2/auth/sessions/all?user_id=${user.id}&except_current=${currentSessionId || ''}`,
        { method: "DELETE" }
      );
      
      if (!response.ok) throw new Error("Failed to revoke sessions");
      
      const data = await response.json();
      toast.success(`${data.revoked_count} sessions revoked`);
      setShowRevokeAllDialog(false);
      loadSessions();
    } catch (error) {
      toast.error("Failed to revoke sessions");
    } finally {
      setRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceInfo, userAgent) => {
    const ua = (userAgent || deviceInfo || "").toLowerCase();
    if (ua.includes("mobile") || ua.includes("android") || ua.includes("iphone")) {
      return <Smartphone className="w-5 h-5" />;
    }
    if (ua.includes("tablet") || ua.includes("ipad")) {
      return <Tablet className="w-5 h-5" />;
    }
    return <Monitor className="w-5 h-5" />;
  };

  const getDeviceName = (deviceInfo, userAgent) => {
    const ua = userAgent || deviceInfo || "Unknown Device";
    
    // Parse browser
    let browser = "Unknown Browser";
    if (ua.includes("Chrome")) browser = "Chrome";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Safari")) browser = "Safari";
    else if (ua.includes("Edge")) browser = "Edge";
    
    // Parse OS
    let os = "Unknown OS";
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac")) os = "macOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
    
    return `${browser} on ${os}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Active Sessions</h1>
          <p className="text-muted-foreground">
            Manage devices signed into your admin account
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadSessions}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {sessions.length > 1 && (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowRevokeAllDialog(true)}
              data-testid="revoke-all-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out All Others
            </Button>
          )}
        </div>
      </div>

      {/* Current Session Alert */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>Current Session</AlertTitle>
        <AlertDescription>
          Your current session is highlighted below. Revoking other sessions will not affect this one.
        </AlertDescription>
      </Alert>

      {/* Sessions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            {sessions.length} Active Session{sessions.length !== 1 ? "s" : ""}
          </CardTitle>
          <CardDescription>
            These devices are currently signed into your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => {
                const isCurrentSession = session.id === currentSessionId;
                
                return (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isCurrentSession 
                        ? "bg-primary/5 border-primary/20" 
                        : "bg-muted/30"
                    }`}
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCurrentSession 
                          ? "bg-primary/10 text-primary" 
                          : "bg-muted text-muted-foreground"
                      }`}>
                        {getDeviceIcon(session.device_info, session.user_agent)}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {getDeviceName(session.device_info, session.user_agent)}
                          </span>
                          {isCurrentSession && (
                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                              Current Session
                            </Badge>
                          )}
                          {session.is_mfa_verified && (
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">
                              <Shield className="w-3 h-3 mr-1" />
                              MFA Verified
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          {session.ip_address && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {session.ip_address}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Active {getTimeAgo(session.last_activity)}
                          </span>
                        </div>
                        
                        <p className="text-xs text-muted-foreground mt-1">
                          Signed in {formatDate(session.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    {!isCurrentSession && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revokeSession(session.id)}
                        disabled={revoking === session.id}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`revoke-${session.id}`}
                      >
                        {revoking === session.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <LogOut className="w-4 h-4 mr-2" />
                            Revoke
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card>
        <CardHeader>
          <CardTitle>Session Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
              <div>
                <p className="font-medium">Session Expiration</p>
                <p className="text-sm text-muted-foreground">
                  Sessions automatically expire after 7 days of inactivity
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
              <div>
                <p className="font-medium">IP Tracking</p>
                <p className="text-sm text-muted-foreground">
                  We track the IP address of each session for security
                </p>
              </div>
            </div>
          </div>
          
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              If you notice any sessions you don't recognize, revoke them immediately 
              and consider changing your password and enabling MFA.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Revoke All Dialog */}
      <Dialog open={showRevokeAllDialog} onOpenChange={setShowRevokeAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5" />
              Sign Out All Other Sessions
            </DialogTitle>
            <DialogDescription>
              This will sign out all devices except the one you're currently using.
              You will stay signed in on this device.
            </DialogDescription>
          </DialogHeader>
          
          <Alert className="my-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{sessions.length - 1}</strong> other session(s) will be signed out.
            </AlertDescription>
          </Alert>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeAllDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={revokeAllSessions}
              disabled={revokingAll}
            >
              {revokingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing Out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out All Others
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
